import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Heart, Shield } from 'lucide-react';

export default function Privacy() {
    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950" data-testid="privacy-page">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-lg border-b border-slate-800">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <Link to="/" className="flex items-center gap-2" data-testid="privacy-home-link">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
                                <Heart className="w-5 h-5 text-white" />
                            </div>
                            <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-2">
                                <span className="font-bold text-lg text-white">Autoimmune Pathways</span>
                                <span className="text-xs sm:text-sm text-slate-400">by Me and My Pandas</span>
                            </div>
                        </Link>
                        <Link to="/">
                            <Button variant="ghost" className="text-slate-300 hover:text-white gap-2" data-testid="privacy-back-button">
                                <ArrowLeft className="w-4 h-4" />
                                Back to Home
                            </Button>
                        </Link>
                    </div>
                </div>
            </nav>

            {/* Content */}
            <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
                <div className="max-w-4xl mx-auto">
                    <div className="flex items-center gap-3 mb-8">
                        <Shield className="w-10 h-10 text-indigo-400" />
                        <h1 className="text-3xl sm:text-4xl font-bold text-white">Privacy Policy</h1>
                    </div>

                    <p className="text-slate-400 text-lg mb-8">Last Updated: May 25, 2026</p>

                    <div className="space-y-10 text-slate-300 leading-relaxed">
                        <section>
                            <h2 className="text-2xl font-semibold text-white mb-4">Introduction</h2>
                            <p className="mb-3">
                                Autoimmune Pathways ("Autoimmune Pathways," "we," "our," or "us") respects your privacy and is committed to protecting your personal information.
                            </p>
                            <p className="mb-3">
                                This Privacy Policy explains how we collect, use, disclose, process, and safeguard information when you access or use the Autoimmune Pathways website, mobile application, services, features, content, and related technologies (collectively, the "Service").
                            </p>
                            <p>By using the Service, you consent to the practices described in this Privacy Policy.</p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-semibold text-white mb-4">1. Nature of the Service</h2>
                            <p className="mb-3">
                                Autoimmune Pathways is a consumer wellness and organizational platform intended to help users track symptoms, organize health-related information, identify patterns, and access educational resources.
                            </p>
                            <p className="mb-3">The Service is not a medical provider, healthcare provider, or emergency service.</p>
                            <p>Unless explicitly stated otherwise in a separate written agreement, Autoimmune Pathways may not be subject to HIPAA in all circumstances.</p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-semibold text-white mb-4">2. Information We Collect</h2>
                            <p className="mb-4">We may collect the following categories of information:</p>

                            <h3 className="text-xl font-semibold text-white mb-3">Account Information</h3>
                            <ul className="list-disc list-inside space-y-1 mb-6 ml-2">
                                <li>Name</li>
                                <li>Email address</li>
                                <li>Password (encrypted and hashed)</li>
                                <li>Subscription and billing information</li>
                                <li>Account preferences</li>
                            </ul>

                            <h3 className="text-xl font-semibold text-white mb-3">Health and Wellness Information You Voluntarily Provide</h3>
                            <ul className="list-disc list-inside space-y-1 mb-4 ml-2">
                                <li>Symptom tracking entries</li>
                                <li>Food and nutrition logs</li>
                                <li>Medication and supplement tracking</li>
                                <li>Sleep information</li>
                                <li>Appointment tracking information</li>
                                <li>Lab values and medical timeline information</li>
                                <li>Journal entries and notes</li>
                                <li>Uploaded files or attachments</li>
                                <li>AI chat prompts and related contextual information</li>
                            </ul>
                            <p>All health-related information is voluntarily entered by you.</p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-semibold text-white mb-4">3. Usage and Device Information</h2>
                            <p className="mb-3">We may automatically collect certain technical information, including:</p>
                            <ul className="list-disc list-inside space-y-1 mb-4 ml-2">
                                <li>Device type</li>
                                <li>Operating system</li>
                                <li>Browser type</li>
                                <li>App version</li>
                                <li>General geographic region</li>
                                <li>IP address</li>
                                <li>Crash reports</li>
                                <li>Diagnostic data</li>
                                <li>Feature usage information</li>
                                <li>Session activity</li>
                            </ul>
                            <p className="mb-2">This information helps us:</p>
                            <ul className="list-disc list-inside space-y-1 ml-2">
                                <li>Improve app functionality;</li>
                                <li>Diagnose technical issues;</li>
                                <li>Enhance security;</li>
                                <li>Improve user experience.</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-2xl font-semibold text-white mb-4">4. Analytics and Performance Monitoring</h2>
                            <p className="mb-3">We may use analytics, crash reporting, and diagnostic tools to better understand app performance and user interactions.</p>
                            <p className="mb-2">These tools may collect:</p>
                            <ul className="list-disc list-inside space-y-1 mb-4 ml-2">
                                <li>General usage statistics;</li>
                                <li>Device information;</li>
                                <li>Error reports;</li>
                                <li>Session data.</li>
                            </ul>
                            <p>We make reasonable efforts to minimize the collection of unnecessary personal information through these services.</p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-semibold text-white mb-4">5. AI Features and Processing</h2>
                            <p className="mb-3">The Service may include AI-assisted tools and features.</p>
                            <p className="mb-2">When using AI-related features:</p>
                            <ul className="list-disc list-inside space-y-1 mb-4 ml-2">
                                <li>Your prompts, questions, and related contextual information may be transmitted to third-party AI providers for processing;</li>
                                <li>AI-generated responses are automated and may not always be accurate, complete, or appropriate;</li>
                                <li>We may utilize configurations designed to limit retention or model training where available, but cannot guarantee third-party processing practices beyond our reasonable control.</li>
                            </ul>
                            <p>You should avoid submitting highly sensitive information through AI chat features unless necessary for your intended use of the Service.</p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-semibold text-white mb-4">6. How We Use Your Information</h2>
                            <p className="mb-3">We may use collected information to:</p>
                            <ul className="list-disc list-inside space-y-1 mb-4 ml-2">
                                <li>Provide and maintain the Service;</li>
                                <li>Process subscriptions and payments;</li>
                                <li>Generate visualizations, trends, and correlations;</li>
                                <li>Improve app functionality and user experience;</li>
                                <li>Provide customer support;</li>
                                <li>Respond to inquiries;</li>
                                <li>Deliver account-related communications;</li>
                                <li>Send optional newsletters or educational content (where permitted);</li>
                                <li>Detect fraud, abuse, or security threats;</li>
                                <li>Comply with legal obligations.</li>
                            </ul>
                            <p>We do not sell your personal health-related information to advertisers or data brokers.</p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-semibold text-white mb-4">7. De-Identified and Aggregated Information</h2>
                            <p className="mb-3">We may create and use de-identified, anonymized, or aggregated information for:</p>
                            <ul className="list-disc list-inside space-y-1 mb-4 ml-2">
                                <li>Analytics;</li>
                                <li>Research;</li>
                                <li>Security monitoring;</li>
                                <li>Service improvement;</li>
                                <li>Product development;</li>
                                <li>Statistical analysis.</li>
                            </ul>
                            <p>Such information is designed not to reasonably identify individual users.</p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-semibold text-white mb-4">8. Community Features</h2>
                            <p className="mb-3">If you participate in community features, forums, or discussions:</p>
                            <ul className="list-disc list-inside space-y-1 mb-4 ml-2">
                                <li>Information you voluntarily share may be visible to other users;</li>
                                <li>You are responsible for the content you choose to disclose publicly;</li>
                                <li>We encourage users not to post highly sensitive personal information publicly.</li>
                            </ul>
                            <p>We are not responsible for how other users may use or interpret publicly shared information.</p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-semibold text-white mb-4">9. Subscription and Payment Processing</h2>
                            <p className="mb-3">Payments and subscription services may be processed through third-party payment processors.</p>
                            <p className="mb-3">We do not store full payment card information on our systems.</p>
                            <p>Payment processors may collect and process billing information according to their own privacy policies and security practices.</p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-semibold text-white mb-4">10. Third-Party Services</h2>
                            <p className="mb-3">We may use trusted third-party service providers, including:</p>
                            <ul className="list-disc list-inside space-y-1 mb-4 ml-2">
                                <li>Cloud hosting providers;</li>
                                <li>Payment processors;</li>
                                <li>AI service providers;</li>
                                <li>Analytics providers;</li>
                                <li>Customer support tools;</li>
                                <li>App store platforms.</li>
                            </ul>
                            <p className="mb-3">These providers may process information on our behalf solely to support operation of the Service.</p>
                            <p className="mb-3">We make reasonable efforts to work with reputable vendors that maintain commercially reasonable security measures.</p>
                            <p>However, we cannot guarantee the practices, security, or performance of third-party services.</p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-semibold text-white mb-4">11. Apple iOS Users</h2>
                            <p className="mb-3">At this time, Autoimmune Pathways does not use data collected from iOS users for cross-app advertising tracking purposes.</p>
                            <p className="mb-2">We currently use essential app functionality technologies, including:</p>
                            <ul className="list-disc list-inside space-y-1 mb-4 ml-2">
                                <li>Authentication management;</li>
                                <li>Session maintenance;</li>
                                <li>User preference storage;</li>
                                <li>Subscription verification.</li>
                            </ul>
                            <p>Practices may evolve as features develop, and users will be notified of material changes through updates to this Privacy Policy.</p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-semibold text-white mb-4">12. Data Security</h2>
                            <p className="mb-3">We implement commercially reasonable administrative, technical, and organizational safeguards designed to help protect user information, including:</p>
                            <ul className="list-disc list-inside space-y-1 mb-4 ml-2">
                                <li>Encryption in transit using TLS/SSL;</li>
                                <li>Password hashing and secure authentication practices;</li>
                                <li>Access controls and permissions management;</li>
                                <li>Security monitoring;</li>
                                <li>Secure cloud infrastructure;</li>
                                <li>Periodic software and security updates.</li>
                            </ul>
                            <p className="mb-3">However, no method of internet transmission, electronic storage, or security system is completely secure.</p>
                            <p>Accordingly, we cannot guarantee absolute security of information.</p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-semibold text-white mb-4">13. Data Retention</h2>
                            <p className="mb-3">We retain information for as long as reasonably necessary to:</p>
                            <ul className="list-disc list-inside space-y-1 mb-4 ml-2">
                                <li>Provide the Service;</li>
                                <li>Maintain operational functionality;</li>
                                <li>Comply with legal obligations;</li>
                                <li>Resolve disputes;</li>
                                <li>Enforce agreements;</li>
                                <li>Prevent fraud or abuse.</li>
                            </ul>
                            <p>Certain information may remain in backups, logs, archives, or security systems for a commercially reasonable period after deletion requests.</p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-semibold text-white mb-4">14. Account Deletion and Data Requests</h2>
                            <p className="mb-3">You may:</p>
                            <ul className="list-disc list-inside space-y-1 mb-4 ml-2">
                                <li>Access your information;</li>
                                <li>Export your data;</li>
                                <li>Request correction of inaccurate information;</li>
                                <li>Request deletion of your account.</li>
                            </ul>
                            <p className="mb-3">Upon verified deletion requests, we will delete or de-identify personal information within a commercially reasonable timeframe, except where retention is necessary for:</p>
                            <ul className="list-disc list-inside space-y-1 mb-4 ml-2">
                                <li>Legal obligations;</li>
                                <li>Fraud prevention;</li>
                                <li>Security purposes;</li>
                                <li>Operational backups;</li>
                                <li>Enforcement of agreements.</li>
                            </ul>
                            <p>
                                To request account deletion, contact:{' '}
                                <a href="mailto:contact@meandmypandas.com" className="text-indigo-400 hover:text-indigo-300 underline">
                                    contact@meandmypandas.com
                                </a>
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-semibold text-white mb-4">15. International Users</h2>
                            <p className="mb-3">The Service is operated primarily within the United States.</p>
                            <p className="mb-3">By using the Service, you understand and agree that your information may be transferred to, processed, and stored in the United States or other jurisdictions where our service providers operate.</p>
                            <p>Privacy laws in these jurisdictions may differ from those in your country of residence.</p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-semibold text-white mb-4">16. Your Privacy Rights</h2>
                            <p className="mb-3">Depending on your jurisdiction, you may have rights to:</p>
                            <ul className="list-disc list-inside space-y-1 mb-4 ml-2">
                                <li>Access personal information;</li>
                                <li>Correct inaccurate information;</li>
                                <li>Request deletion;</li>
                                <li>Request data portability;</li>
                                <li>Opt out of certain communications;</li>
                                <li>Withdraw consent where applicable.</li>
                            </ul>
                            <p>We may require verification of identity before processing certain requests.</p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-semibold text-white mb-4">17. Marketing Communications</h2>
                            <p className="mb-3">You may opt out of promotional or marketing emails at any time using unsubscribe links or by contacting us directly.</p>
                            <p className="mb-2">We may still send non-promotional communications related to:</p>
                            <ul className="list-disc list-inside space-y-1 ml-2">
                                <li>Account management;</li>
                                <li>Billing;</li>
                                <li>Security;</li>
                                <li>Service functionality;</li>
                                <li>Legal notices.</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-2xl font-semibold text-white mb-4">18. Children's Privacy</h2>
                            <p className="mb-3">The Service is intended only for individuals age 18 and older.</p>
                            <p className="mb-3">We do not knowingly collect personal information directly from children under 18.</p>
                            <p>If we become aware that information has been submitted by a minor in violation of this Policy, we may remove such information and terminate associated accounts.</p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-semibold text-white mb-4">19. Security Incidents and Breach Notifications</h2>
                            <p>In the event of a security incident involving personal information, we may notify affected users electronically, through the Service, by email, or by other methods as required by applicable law.</p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-semibold text-white mb-4">20. Changes to This Privacy Policy</h2>
                            <p className="mb-3">We may update this Privacy Policy periodically.</p>
                            <p className="mb-2">Material changes may be communicated through:</p>
                            <ul className="list-disc list-inside space-y-1 mb-4 ml-2">
                                <li>App notifications;</li>
                                <li>Email communications;</li>
                                <li>Website notices;</li>
                                <li>Updated posting dates.</li>
                            </ul>
                            <p>Continued use of the Service after changes become effective constitutes acceptance of the revised Privacy Policy.</p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-semibold text-white mb-4">21. Contact Information</h2>
                            <p className="mb-3">If you have questions regarding this Privacy Policy or our privacy practices, please contact:</p>
                            <p className="mb-1 text-white font-medium">Me and My PANDAS LLC</p>
                            <p>
                                Email:{' '}
                                <a href="mailto:contact@meandmypandas.com" className="text-indigo-400 hover:text-indigo-300 underline" data-testid="privacy-contact-email">
                                    contact@meandmypandas.com
                                </a>
                            </p>
                        </section>
                    </div>
                </div>
            </div>
        </div>
    );
}
